import type { DocumentType } from "~/api/prismic/enums"

import type { ContentRelationship, PrismicDocumentWithUid } from "."
import type { Author } from "./author"
import type { Category } from "./category"
import type { ColorScheme } from "./color-scheme"

interface SliceBase {
  id?: string
  items: Record<string, unknown>[]
  slice_label: string | null
  slice_type: `${SliceTypes}`
}

export enum SliceTypes {
  TEXT = "text",
  HIGHLIGHTED_QUOTE = "highlighted_quote",
  QUOTE = "quote",
  CHART_EMBED = "chart_embed",
  IMAGE_GALLERY = "image_gallery",
  SINGLE_IMAGE = "full-width_image",
  CSV_TABLES = "tables",
  HTML_EMBED = "html_embed",
  ALERT = "alert",
}

export interface RichTextSlice extends SliceBase {
  slice_type: `${SliceTypes.TEXT}`
  primary: {
    text: prismic.RichTextField
    text_size: "small" | "normal" | "large"
  }
}
export interface AlertTextSlice extends SliceBase {
  slice_type: `${SliceTypes.ALERT}`
  primary: {
    text: prismic.RichTextField
    color: "blue" | "yellow" | "red"
  }
}

export interface HighlightedQuoteSlice extends SliceBase {
  slice_type: `${SliceTypes.HIGHLIGHTED_QUOTE}`
  primary: {
    quote_text: prismic.RichTextField
  }
}

export interface AuthorQuoteSlice extends SliceBase {
  slice_type: `${SliceTypes.QUOTE}`
  primary: {
    quote: prismic.RichTextField
    author_name: prismic.RichTextField
    author_portrait: prismic.ImageDetails
  }
}

export interface ChartEmbedSlice extends SliceBase {
  slice_type: `${SliceTypes.CHART_EMBED}`
  primary: {
    iframe_title: string | null
    iframe_url: string
  }
}

export interface ImageGallerySlice extends SliceBase {
  slice_type: `${SliceTypes.IMAGE_GALLERY}`
  items: {
    image_captions: prismic.RichTextField
    credit: prismic.RichTextField
    gallery_image: prismic.ImageDetails
  }[]
  primary: {
    gallery_name: prismic.RichTextField
    display_type: "carousel" | "tiles"
    max_tiles_per_row_mobile: string | null
    max_tiles_per_row_desktop: string | null
  }
}

export enum SingleImageSizes {
  TEXT_COLUMN = "Text-column",
  ORIGINAL = "Original size",
  SCREEN = "Screen",
}

export interface SingleImageSlice extends SliceBase {
  slice_type: `${SliceTypes.SINGLE_IMAGE}`
  primary: {
    image: prismic.ImageDetails
    image_size: `${SingleImageSizes}`
    with_parallax: boolean
    caption: prismic.RichTextField
    credit: prismic.RichTextField
  }
}

export interface CSVTablesSlice extends SliceBase {
  slice_type: `${SliceTypes.CSV_TABLES}`
  slice_label: null
  primary: {
    show_side_by_side: boolean
  }
  items: {
    table_description: prismic.RichTextField
    csv: {
      link_type: string
      name: string
      kind: string
      url: string
      size: string
    }
    has_header: boolean
  }[]
}
export interface HTMLEmbedSlice extends SliceBase {
  slice_type: `${SliceTypes.HTML_EMBED}`
  slice_label: null
  primary: {
    iframe_title: string
    html: prismic.RichTextField
    add_padding: boolean | null
    change_background_color: boolean | null
    background_color: string | null
  }
}

export type ArticleSlice =
  | RichTextSlice
  | AlertTextSlice
  | HighlightedQuoteSlice
  | AuthorQuoteSlice
  | ChartEmbedSlice
  | ImageGallerySlice
  | SingleImageSlice
  | CSVTablesSlice
  | HTMLEmbedSlice

export type Footnote = {
  number: number
  ref_title: string
  additional_info: prismic.RichTextField
  link_to_ref: {
    link_type: string
    url?: string
    target?: string
  }
}

type AuthorLink = ContentRelationship<
  Author,
  "name" | "twitter_handle" | "instagram"
>

export type Article = PrismicDocumentWithUid<
  DocumentType.ARTICLE,
  {
    title: prismic.RichTextField
    subtitle: prismic.RichTextField
    /** @deprecated Use `authors` instead */
    author: AuthorLink
    authors: { link: AuthorLink }[]
    featured: boolean
    body: ArticleSlice[]
    banner_image: prismic.ImageDetails
    banner_image_credits: prismic.RichTextField
    color_scheme: ContentRelationship<ColorScheme, "name">
    meta_description: string
    footnotes: Footnote[]
    categories: { category_link: ContentRelationship<Category, "name"> }[]
    audio_url: string | null
  }
>
